// 地點資訊展開的卡牌
import React, { useRef, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Button,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconBolt,
  IconClipboardCheck,
  IconClock,
  IconGasStation,
  IconParking,
  IconPhone,
  IconSticker2,
  IconTool,
  IconVector,
  IconX,
} from '@tabler/icons-react';
import { format, parseISO } from 'date-fns';
import { getServiceStatusColor } from '../../../const/service_status_mapping.ts';
import { dayMap, formatTimeLabel } from '../../../utils/DateUtil.ts';
import { parseRate } from '../../../utils/ParkingRateCardUtils.ts';
import { getConnectorTypeLabel } from '../../../const/charging_connector_type_mapping.ts';
import { useTheme } from '../../../context/ThemeProvider.tsx';
import { PlaceWithServices } from '../../../models/Place.ts';
import { EVSE } from '../../../models/EVSE.ts';
import { ChargingServiceConnectorSummary } from '../../../models/ChargingServiceConnectorSummary.ts';
import { useBrands } from '../../../hooks/brands/useBrands.ts';
import { useChargingServicesInformation } from '../../../hooks/places/useChargingServicesInformation.ts';
import { useNavigate } from 'react-router-dom';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { PowerType } from '../../../models/ChargingConnector.ts';
import { ChargingService } from '../../../models/ChargingService.ts';
import { ParkingService } from '../../../models/ParkingService.ts';
import { fuelTypeMap } from '../../../const/fueling_service_enum_mapping.ts';
import { FuelingService } from '../../../models/FuelingService.ts';
import { ServiceType } from '../../../models/ServiceType.ts';
import {
  AdditionalRules,
  MaximumFeeDetail,
  ParkingRate,
  RateDetail,
} from '../../../models/ParkingRate.ts';

// ─── 停車費率 helpers ──────────────────────────────────────────────────────────

const DOW_ORDER_PSS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const;

function formatRate(rate: RateDetail): string[] {
  const parsed = parseRate(rate);
  if (parsed.kind === 'free') return ['免費'];
  if (parsed.kind === 'per_entry') return [`${parsed.fee} 元 / 次`];
  return parsed.tiers.map((t) => {
    const feeText = t.isFree
      ? '免費'
      : `每 ${t.intervalMinutes} 分鐘 ${t.fee} 元`;
    return t.timeLabel ? `${t.timeLabel} ${feeText}` : feeText;
  });
}

function formatAdditionalRules(rules: AdditionalRules): string {
  const parts: string[] = [];
  if (rules.entryBufferTime !== null)
    parts.push(`進場緩衝 ${rules.entryBufferTime} 分鐘`);
  if (rules.isResetEveryDay) parts.push('跨日重新計算');
  return parts.join('\n') || '-';
}

function formatMaximumFee(fee: MaximumFeeDetail | null): string {
  if (!fee) return '-';
  if (fee.type === 'WHOLE_DAY') return `全天最高 ${fee.fee} 元`;
  if (fee.type === 'FIRST_UNIT')
    return `首 ${fee.intervalMinutes} 分鐘最高 ${fee.fee} 元`;
  return `每 ${fee.intervalMinutes} 分鐘最高 ${fee.fee} 元`;
}

/**
 * 給定 parkingRate，回傳「此時此刻」套用的時段資訊。
 * - 回傳 null：無任何有效規則（effectivePeriod 都不包住現在）
 * - 回傳 rate === null：有效規則存在，但當前時間落在 gap（無費率時段）
 * - 回傳 rate !== null：正常費率時段
 * - maximumFee 與 rate 獨立查找，可各自為 null
 *
 * 優先順序：specificRule > regularRule
 */
function getCurrentRateSlot(parkingRate: ParkingRate): {
  startTime: string;
  endTime: string;
  rate: RateDetail | null;
  additionalRules: AdditionalRules;
  maximumFee: MaximumFeeDetail[];
} | null {
  const now = new Date();
  const nowTime = format(now, 'HH:mm');
  const todayStr = format(now, 'yyyy-MM-dd');
  const todayDow = DOW_ORDER_PSS[(now.getDay() + 6) % 7];

  const isInEffectivePeriod = (start: string, end: string) =>
    new Date(start) <= now && now <= new Date(end);

  // 1. specificRule（優先）
  const activeSpecific = parkingRate.specificRules.find((r) =>
    isInEffectivePeriod(
      r.effectivePeriod.startDateTime,
      r.effectivePeriod.endDateTime,
    ),
  );
  if (activeSpecific) {
    let rateResult: {
      rate: RateDetail;
      startTime: string;
      endTime: string;
    } | null = null;
    for (const period of activeSpecific.ratePeriods) {
      for (const slot of period.dateTimeSlots) {
        if (format(parseISO(slot.startDateTime), 'yyyy-MM-dd') !== todayStr)
          continue;
        const start = format(parseISO(slot.startDateTime), 'HH:mm');
        const end = format(parseISO(slot.endDateTime), 'HH:mm');
        if (start <= nowTime && nowTime <= end) {
          rateResult = { rate: period.rate, startTime: start, endTime: end };
          break;
        }
      }
      if (rateResult) break;
    }

    const maximumFee: MaximumFeeDetail[] = [];
    for (const period of activeSpecific.maximumFeeRules) {
      for (const slot of period.dateTimeSlots) {
        if (format(parseISO(slot.startDateTime), 'yyyy-MM-dd') !== todayStr)
          continue;
        const start = format(parseISO(slot.startDateTime), 'HH:mm');
        const end = format(parseISO(slot.endDateTime), 'HH:mm');
        if (start <= nowTime && nowTime <= end) {
          maximumFee.push(...period.maximumFee);
          break;
        }
      }
    }

    return {
      startTime: rateResult?.startTime ?? '00:00',
      endTime: rateResult?.endTime ?? '23:59',
      rate: rateResult?.rate ?? null,
      additionalRules: activeSpecific.additionalRules,
      maximumFee,
    };
  }

  // 2. regularRule
  const activeRegular = parkingRate.regularRules.find((r) =>
    isInEffectivePeriod(
      r.effectivePeriod.startDateTime,
      r.effectivePeriod.endDateTime,
    ),
  );
  if (activeRegular) {
    let rateResult: {
      rate: RateDetail;
      startTime: string;
      endTime: string;
    } | null = null;
    for (const period of activeRegular.ratePeriods) {
      for (const slot of period.dayOfWeekTimeSlots) {
        if (slot.startDayOfWeek !== todayDow) continue;
        const start = formatTimeLabel(slot.startTime);
        const end = formatTimeLabel(slot.endTime);
        if (start <= nowTime && nowTime <= end) {
          rateResult = { rate: period.rate, startTime: start, endTime: end };
          break;
        }
      }
      if (rateResult) break;
    }

    const maximumFee: MaximumFeeDetail[] = [];
    for (const period of activeRegular.maximumFeeRules) {
      for (const slot of period.dayOfWeekTimeSlots) {
        if (slot.startDayOfWeek !== todayDow) continue;
        const start = formatTimeLabel(slot.startTime);
        const end = formatTimeLabel(slot.endTime);
        if (start <= nowTime && nowTime <= end) {
          maximumFee.push(...period.maximumFee);
          break;
        }
      }
    }

    return {
      startTime: rateResult?.startTime ?? '00:00',
      endTime: rateResult?.endTime ?? '23:59',
      rate: rateResult?.rate ?? null,
      additionalRules: activeRegular.additionalRules,
      maximumFee,
    };
  }

  return null;
}

// ──────────────────────────────────────────────────────────────────────────────

const SERVICE_ORDER: ServiceType[] = [
  'PARKING',
  'CHARGING',
  'FUELING',
  'MAINTENANCE',
  'INSPECTION',
];

const phoneUtil = PhoneNumberUtil.getInstance();

interface PlaceServicesSummaryProps {
  isNearbyPlacesListOpen: boolean;
  selectedLocation: PlaceWithServices;
  setSelectedLocation?: (place: PlaceWithServices) => void;
}

const PlaceServicesSummary: React.FC<PlaceServicesSummaryProps> = ({
  isNearbyPlacesListOpen,
  selectedLocation,
  setSelectedLocation,
}) => {
  const { colorScheme } = useTheme();

  // 手風琴展開狀態純粹是這個元件內部的顯示狀態，不需要提升到父層
  const [operationHoursOpen, setOperationHoursOpen] = useState<string | null>(
    null,
  );
  const [parkingRateOpen, setParkingRateOpen] = useState<string | null>(null);

  // 記錄 選定地點的服務數量 (用以判斷是否需要 render 分隔線)
  const [showTopShadow, setShowTopShadow] = useState(false); // 處理 scroll area 的陰影 (top)
  const [showBottomShadow, setShowBottomShadow] = useState(false); // 處理 scroll area 的陰影 (bottom)
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const numOfServices = selectedLocation.services.length;

  // 處理 scroll area 的陰影
  const handleScroll = () => {
    if (!scrollRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

    setShowTopShadow(scrollTop > 0);
    setShowBottomShadow(scrollTop + clientHeight < scrollHeight - 1);
  };

  const services = selectedLocation.services ?? [];

  const chargingServices = services.filter(
    (s) => s.serviceType === 'CHARGING',
  ) as ChargingService[];

  // 從 選定地點下的所有充電服務拿到其充電樁 & 槍的資訊
  const chargingServiceQueries = useChargingServicesInformation(
    chargingServices,
    selectedLocation?.id,
  );

  // 從 選定地點下的所有服務拿到其對應的品牌 ID
  const brandQueries = useBrands(
    selectedLocation?.services,
    selectedLocation?.id,
  );

  // 把充電樁 & 槍的資訊整理成統計的 summary
  const chargingDetailsMap = selectedLocation?.services?.reduce(
    (acc, service, index) => {
      const evses = chargingServiceQueries[index]?.data as EVSE[] | undefined;
      if (!evses) return acc;

      // 收集該 service 下的所有 connectors (忽略 EVSE 層的資訊)
      const allConnectors = evses.flatMap((evse) =>
        Object.values(evse.connectors),
      );

      // 按照 connectorType + powerType + maxPower 做分組
      const groupedMap = allConnectors.reduce(
        (group, connector) => {
          const key = `${connector.connectorType}-${connector.powerType}-${connector.maxPower.value}-${connector.maxPower.unit}`;
          if (!group[key]) {
            group[key] = {
              connectorType: connector.connectorType,
              powerType: connector.powerType,
              maxPower: connector.maxPower,
              number: '1',
            };
          } else {
            group[key].number = (parseInt(group[key].number) + 1).toString();
          }
          return group;
        },
        {} as Record<string, ChargingServiceConnectorSummary>,
      );

      // 排序邏輯：依 connectorType 排序
      acc[service.id] = Array.from(Object.values(groupedMap)).sort((a, b) =>
        a.connectorType.localeCompare(b.connectorType),
      );

      return acc;
    },
    {} as Record<string, ChargingServiceConnectorSummary[]>,
  );

  const isChargingServiceLoading = chargingServiceQueries.some(
    (q) => q.isLoading,
  );
  const isChargingServiceError = chargingServiceQueries.some((q) => q.isError);

  const handleInfoWindowClose = () => {
    setSelectedLocation?.(null as PlaceWithServices);
  };

  const handlePlaceInformationPageClick = (place: PlaceWithServices) => {
    navigate(`/places/${place.id}`);
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: '78px',
        left: isNearbyPlacesListOpen ? '414px' : '38px',
        bottom: '20px',
        width: '340px',
        zIndex: 1000,
        borderRadius: 20,
        backgroundColor: colorScheme === 'light' ? '#ffffff' : '#1a1a1a',
        boxShadow: '0 2px 2px rgba(0, 0, 0, 0.1)',
        maxWidth: '350px',
        minWidth: '300px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* 固定在頂部的 header */}
      <Group
        style={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          paddingInline: 20,
          borderRadius: '20px 20px 0 0',
          height: 46,
          alignItems: 'center',
          backgroundColor: colorScheme === 'light' ? '#ffffff' : '#1a1a1a',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Tooltip
          label={selectedLocation.name}
          position={'top'}
          offset={0}
          openDelay={200}
          color={'rgba(120, 120, 120, 0.85)'}
          style={{
            fontSize: 12,
          }}
        >
          <Text
            truncate={true}
            style={{
              fontSize: 16,
              fontWeight: 700,
              fontFamily: 'sans-serif',
              marginTop: 15,
              lineHeight: '20px',
              marginBottom: 11,
              maxWidth: 256,
            }}
          >
            {selectedLocation.name}
          </Text>
        </Tooltip>
        <ActionIcon variant={'transparent'}>
          <IconX
            onClick={handleInfoWindowClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
              color: '#868e96',
              padding: '4px',
              lineHeight: '1',
            }}
          />
        </ActionIcon>
      </Group>

      {/* 可滾動內容區 */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{
          overflowY: 'auto',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 上方陰影 */}
        {showTopShadow && (
          <div
            style={{
              position: 'absolute',
              width: 340,
              justifyItems: 'left',
              top: 46,
              left: 0,
              right: 0,
              height: '15px',
              background:
                'linear-gradient(to bottom, rgba(0, 0, 0, 0.1), transparent)',
              pointerEvents: 'none',
              zIndex: 10,
            }}
          />
        )}

        {/* 卡牌: 服務內容 */}
        <Stack
          align={'stretch'}
          style={{
            paddingInline: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
          }}
        >
          {/* 載入中 */}
          {isChargingServiceLoading && (
            <LoadingOverlay
              visible={true}
              overlayProps={{ radius: 'sm', blur: 1 }}
              loaderProps={{
                color: 'rgb(67,154,255)',
                type: 'bars',
                size: 'sm',
              }}
            />
          )}

          {/* 錯誤 */}
          {isChargingServiceError && <Text>Error loading some services</Text>}

          {[...selectedLocation.services]
            .sort(
              (a, b) =>
                SERVICE_ORDER.indexOf(a.serviceType) -
                SERVICE_ORDER.indexOf(b.serviceType),
            )
            .map((service, index) => {
              if (service.serviceType === 'CHARGING') {
                return (
                  <Stack
                    key={index}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 15,
                      paddingTop: index !== 0 ? 15 : 6,
                      paddingBottom: 20,
                      borderBottom:
                        index !== numOfServices - 1 && colorScheme === 'light'
                          ? '1px solid #DEE2E6'
                          : index !== numOfServices - 1 &&
                              colorScheme === 'dark'
                            ? '1px solid #737373ff'
                            : null,
                    }}
                  >
                    {/* 服務狀態 與 名稱 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: 300,
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: `${getServiceStatusColor(service.serviceStatus)}`,
                        }}
                      />
                      <IconBolt size={16} />
                      <Tooltip
                        label={service.name}
                        position={'bottom'}
                        offset={0}
                        openDelay={200}
                        color={'rgba(120, 120, 120, 0.85)'}
                        style={{
                          fontSize: 12,
                        }}
                      >
                        <Text
                          truncate={'end'}
                          style={{
                            fontWeight: 700,
                            fontSize: 14,
                            fontFamily: 'sans-serif',
                            maxWidth: 214,
                            maxHeight: 20,
                          }}
                        >
                          {service.name}
                        </Text>
                      </Tooltip>
                    </Group>

                    {/* 服務提供商 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: '100%',
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <IconVector
                        size={16}
                        color={colorScheme === 'light' ? 'black' : 'white'}
                      />
                      <Text
                        style={{
                          fontWeight: 400,
                          fontSize: 14,
                          fontFamily: 'sans-serif',
                        }}
                      >
                        {brandQueries[index]?.data?.name ?? '-'}
                      </Text>
                    </Group>

                    {/* 電話號碼 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: '100%',
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <IconPhone
                        size={16}
                        color={colorScheme === 'light' ? 'black' : 'white'}
                      />
                      <Text
                        style={{
                          fontWeight: 400,
                          fontSize: 14,
                          fontFamily: 'sans-serif',
                        }}
                      >
                        {service.phoneNumber
                          ? (() => {
                              if (
                                service.phoneNumber.countryCode === '' ||
                                service.phoneNumber.number === ''
                              ) {
                                return '-';
                              }

                              const parsedPhoneNumber = phoneUtil.parse(
                                `${service.phoneNumber.countryCode}${service.phoneNumber.number}`,
                              );

                              return (
                                <>
                                  {'('}
                                  {service.phoneNumber.countryCode}
                                  {') '}
                                  {phoneUtil.format(
                                    parsedPhoneNumber,
                                    PhoneNumberFormat.NATIONAL,
                                  )}
                                </>
                              );
                            })()
                          : '-'}
                      </Text>
                    </Group>

                    {/* 營業時間 */}
                    <Accordion
                      variant="unstyled"
                      value={operationHoursOpen}
                      onChange={setOperationHoursOpen}
                      styles={{
                        root: {
                          padding: 0,
                          gap: 8,
                          width: 168,
                        },
                        label: {
                          height: 20,
                          padding: 0,
                          fontSize: 14,
                          marginRight: 8,
                        },
                        control: {
                          padding: 0,
                        },
                        content: {
                          fontSize: 12,
                          fontWeight: 400,
                          paddingLeft: 24,
                          paddingRight: 0,
                          marginTop: 10,
                          marginBottom: 5,
                          paddingBottom: 0,
                        },
                        icon: {
                          marginRight: 8,
                        },
                      }}
                    >
                      <Accordion.Item value={'operation-hours'}>
                        <Accordion.Control
                          icon={
                            <IconClock
                              size={16}
                              color={
                                colorScheme === 'light' ? 'black' : 'white'
                              }
                            />
                          }
                        >
                          查看營業時間
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack gap={10}>
                            {Object.entries(dayMap).map(([key, { label }]) => {
                              const schedule =
                                service.operationHours?.weeklySchedule ?? [];
                              const day = schedule.find(
                                (w) => w.dayOfWeek === key,
                              );

                              // day 有可能不存在
                              const isUnknown = day?.isOpen === 'UNKNOWN';
                              const isOpen = day?.isOpen === 'OPEN';

                              const start =
                                day?.timeSlots?.[0]?.startTime
                                  ?.split(':')
                                  .slice(0, 2)
                                  .join(':') ?? '--:--';

                              const end =
                                day?.timeSlots?.[0]?.endTime
                                  ?.split(':')
                                  .slice(0, 2)
                                  .join(':') ?? '--:--';

                              return (
                                <Group key={key} gap={3}>
                                  {isOpen ? (
                                    <>
                                      <Text
                                        style={{
                                          fontSize: 12,
                                          whiteSpace: 'pre',
                                        }}
                                      >
                                        {label}
                                        {'   '}
                                        {start}
                                        {' - '}
                                        {end}
                                      </Text>
                                    </>
                                  ) : isUnknown ? (
                                    <Text
                                      style={{
                                        color: 'grey',
                                        fontSize: 12,
                                        whiteSpace: 'pre',
                                      }}
                                    >
                                      {label}
                                      {'   '}
                                      未知
                                    </Text>
                                  ) : (
                                    <Text
                                      style={{
                                        color: 'grey',
                                        fontSize: 12,
                                        whiteSpace: 'pre',
                                      }}
                                    >
                                      {label}
                                      {'   '}
                                      休息
                                    </Text>
                                  )}
                                </Group>
                              );
                            })}
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>

                    {/* 備註文字 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: '100%',
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <IconSticker2
                        size={16}
                        color={colorScheme === 'light' ? 'black' : 'white'}
                      />
                      <Tooltip
                        multiline
                        label={
                          <Text
                            style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}
                          >
                            {service.notes}
                          </Text>
                        }
                        disabled={!service.notes}
                        styles={{
                          tooltip: {
                            maxWidth: 300,
                          },
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 14,
                            fontWeight: 400,
                            maxWidth: 276,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {service.notes
                            ? `${service.notes.split('\n')[0]} ...`
                            : '-'}
                        </Text>
                      </Tooltip>
                    </Group>

                    {/* 充電槍的統計數據 */}
                    {(chargingDetailsMap?.[service.id] ?? []).map(
                      (connectorTypeSummary) => (
                        <Group
                          justify={'space-between'}
                          style={{
                            width: 300,
                            height: 32,
                            border:
                              colorScheme === 'light'
                                ? '1px solid #DEE2E6'
                                : '1px solid #737373ff',
                            borderRadius: 4,
                            paddingInline: 16,
                            gap: 0,
                          }}
                        >
                          <Group
                            gap={8}
                            style={{
                              maxHeight: 19,
                              maxWidth: 230,
                              overflow: 'hidden',
                            }}
                          >
                            <IconBolt
                              color={'#228BE6'}
                              style={{
                                backgroundColor: '#228BE61A',
                                width: 16,
                                height: 16,
                              }}
                            />
                            <div style={{ fontSize: 12, fontWeight: 400 }}>
                              {getConnectorTypeLabel(
                                connectorTypeSummary.connectorType,
                              )}
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 400 }}>
                              ·
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 400 }}>
                              {connectorTypeSummary.maxPower.value +
                                connectorTypeSummary.maxPower.unit}
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 400 }}>
                              ·
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 400 }}>
                              {connectorTypeSummary.powerType !==
                              ('UNKNOWN' as PowerType)
                                ? connectorTypeSummary.powerType
                                : '未知'}
                            </div>
                          </Group>
                          <div style={{ fontSize: 12, fontWeight: 400 }}>
                            {`數量 ${connectorTypeSummary.number}`}
                          </div>
                        </Group>
                      ),
                    )}
                  </Stack>
                );
              } else if (service.serviceType === 'PARKING') {
                return (
                  <Stack
                    key={index}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 15,
                      paddingTop: numOfServices !== 0 ? 15 : 6,
                      paddingBottom: 20,
                      borderBottom:
                        index !== numOfServices - 1 && colorScheme === 'light'
                          ? '1px solid #DEE2E6'
                          : index !== numOfServices - 1 &&
                              colorScheme === 'dark'
                            ? '1px solid #737373ff'
                            : null,
                    }}
                  >
                    {/* 服務狀態 與 名稱 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: 300,
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: `${getServiceStatusColor(service.serviceStatus)}`,
                        }}
                      />
                      <IconParking size={16} />
                      <Tooltip
                        label={service.name}
                        position={'bottom'}
                        offset={0}
                        openDelay={200}
                        color={'rgba(120, 120, 120, 0.85)'}
                        style={{
                          fontSize: 12,
                        }}
                      >
                        <Text
                          truncate={'end'}
                          style={{
                            fontWeight: 700,
                            fontSize: 14,
                            fontFamily: 'sans-serif',
                            maxWidth: 214,
                            maxHeight: 20,
                          }}
                        >
                          {service.name}
                        </Text>
                      </Tooltip>
                    </Group>

                    {/* 品牌 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: '100%',
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <IconVector
                        size={16}
                        color={colorScheme === 'light' ? 'black' : 'white'}
                      />
                      <Text
                        style={{
                          fontWeight: 400,
                          fontSize: 14,
                          fontFamily: 'sans-serif',
                        }}
                      >
                        {brandQueries[index]?.data?.name ?? '-'}
                      </Text>
                    </Group>

                    {/* 電話號碼 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: '100%',
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <IconPhone
                        size={16}
                        color={colorScheme === 'light' ? 'black' : 'white'}
                      />
                      <Text
                        style={{
                          fontWeight: 400,
                          fontSize: 14,
                          fontFamily: 'sans-serif',
                        }}
                      >
                        {service.phoneNumber
                          ? (() => {
                              if (
                                service.phoneNumber.countryCode === '' ||
                                service.phoneNumber.number === ''
                              ) {
                                return '-';
                              }

                              const parsedPhoneNumber = phoneUtil.parse(
                                `${service.phoneNumber.countryCode}${service.phoneNumber.number}`,
                              );

                              return (
                                <>
                                  {'('}
                                  {service.phoneNumber.countryCode}
                                  {') '}
                                  {phoneUtil.format(
                                    parsedPhoneNumber,
                                    PhoneNumberFormat.NATIONAL,
                                  )}
                                </>
                              );
                            })()
                          : '-'}
                      </Text>
                    </Group>

                    {/* 營業時間 */}
                    <Accordion
                      variant="unstyled"
                      value={operationHoursOpen}
                      onChange={setOperationHoursOpen}
                      styles={{
                        root: {
                          padding: 0,
                          gap: 8,
                          width: 168,
                        },
                        label: {
                          height: 20,
                          padding: 0,
                          fontSize: 14,
                          marginRight: 8,
                        },
                        control: {
                          padding: 0,
                        },
                        content: {
                          fontSize: 12,
                          fontWeight: 400,
                          paddingLeft: 24,
                          paddingRight: 0,
                          marginTop: 10,
                          marginBottom: 5,
                          paddingBottom: 0,
                        },
                        icon: {
                          marginRight: 8,
                        },
                      }}
                    >
                      <Accordion.Item value={'operation-hours'}>
                        <Accordion.Control
                          icon={
                            <IconClock
                              size={16}
                              color={
                                colorScheme === 'light' ? 'black' : 'white'
                              }
                            />
                          }
                        >
                          查看營業時間
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack gap={10}>
                            {Object.entries(dayMap).map(([key, { label }]) => {
                              const schedule =
                                service.operationHours?.weeklySchedule ?? [];
                              const day = schedule.find(
                                (w) => w.dayOfWeek === key,
                              );

                              // day 有可能不存在
                              const isUnknown = day?.isOpen === 'UNKNOWN';
                              const isOpen = day?.isOpen === 'OPEN';

                              const start =
                                day?.timeSlots?.[0]?.startTime
                                  ?.split(':')
                                  .slice(0, 2)
                                  .join(':') ?? '--:--';

                              const end =
                                day?.timeSlots?.[0]?.endTime
                                  ?.split(':')
                                  .slice(0, 2)
                                  .join(':') ?? '--:--';

                              return (
                                <Group key={key} gap={3}>
                                  {isOpen ? (
                                    <>
                                      <Text
                                        style={{
                                          fontSize: 12,
                                          whiteSpace: 'pre',
                                        }}
                                      >
                                        {label}
                                        {'   '}
                                        {start}
                                        {' - '}
                                        {end}
                                      </Text>
                                    </>
                                  ) : isUnknown ? (
                                    <Text
                                      style={{
                                        color: 'grey',
                                        fontSize: 12,
                                        whiteSpace: 'pre',
                                      }}
                                    >
                                      {label}
                                      {'   '}
                                      未知
                                    </Text>
                                  ) : (
                                    <Text
                                      style={{
                                        color: 'grey',
                                        fontSize: 12,
                                        whiteSpace: 'pre',
                                      }}
                                    >
                                      {label}
                                      {'   '}
                                      休息
                                    </Text>
                                  )}
                                </Group>
                              );
                            })}
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>

                    {/* 停車費率 */}
                    <Accordion
                      variant="unstyled"
                      value={parkingRateOpen}
                      onChange={setParkingRateOpen}
                      styles={{
                        root: {
                          padding: 0,
                          gap: 8,
                        },
                        label: {
                          height: 20,
                          padding: 0,
                          fontSize: 14,
                          marginRight: 8,
                        },
                        control: {
                          padding: 0,
                        },
                        content: {
                          fontSize: 14,
                          fontWeight: 400,
                          paddingLeft: 24,
                          paddingRight: 0,
                          marginTop: 10,
                          marginBottom: 5,
                          paddingBottom: 0,
                        },
                        icon: {
                          marginRight: 8,
                        },
                      }}
                    >
                      <Accordion.Item value={'parking-rate'}>
                        <Accordion.Control
                          icon={
                            <IconClock
                              size={16}
                              color={
                                colorScheme === 'light' ? 'black' : 'white'
                              }
                            />
                          }
                        >
                          查看停車費率
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack gap={10}>
                            {(() => {
                              const slot = getCurrentRateSlot(
                                (service as ParkingService).parkingRate,
                              );
                              if (slot === null) {
                                return (
                                  <Text style={{ color: 'grey', fontSize: 12 }}>
                                    尚未設定費率
                                  </Text>
                                );
                              }
                              return (
                                <Stack gap={16}>
                                  <Stack gap={4}>
                                    <Text
                                      style={{ fontSize: 12, fontWeight: 700 }}
                                    >
                                      當前費率:
                                    </Text>
                                    <Group
                                      gap={4}
                                      align="flex-start"
                                      wrap="nowrap"
                                    >
                                      <Text
                                        style={{
                                          fontSize: 12,
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {slot.startTime} – {slot.endTime}
                                        {' ： '}
                                      </Text>
                                      {slot.rate === null ? (
                                        <Text style={{ fontSize: 12 }}>
                                          未知
                                        </Text>
                                      ) : (
                                        <Stack gap={2}>
                                          {formatRate(slot.rate).map(
                                            (line, i) => (
                                              <Text
                                                key={i}
                                                style={{ fontSize: 12 }}
                                              >
                                                {line}
                                              </Text>
                                            ),
                                          )}
                                        </Stack>
                                      )}
                                    </Group>
                                  </Stack>
                                  <Stack gap={4}>
                                    <Text
                                      style={{ fontSize: 12, fontWeight: 700 }}
                                    >
                                      進階費率資訊：
                                    </Text>
                                    <Text
                                      style={{
                                        fontSize: 12,
                                        whiteSpace: 'pre-line',
                                      }}
                                    >
                                      {formatAdditionalRules(
                                        slot.additionalRules,
                                      )}
                                    </Text>
                                  </Stack>
                                  <Stack gap={4}>
                                    <Text
                                      style={{ fontSize: 12, fontWeight: 700 }}
                                    >
                                      最高收費：
                                    </Text>
                                    <Text
                                      style={{
                                        fontSize: 12,
                                        whiteSpace: 'pre-line',
                                      }}
                                    >
                                      {slot.maximumFee.length === 0
                                        ? '-'
                                        : slot.maximumFee
                                            .map(formatMaximumFee)
                                            .join('\n')}
                                    </Text>
                                  </Stack>
                                </Stack>
                              );
                            })()}
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>

                    {/* 限高 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: '100%',
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <IconSticker2
                        size={16}
                        color={colorScheme === 'light' ? 'black' : 'white'}
                      />
                      <Text
                        style={{
                          fontWeight: 400,
                          fontSize: 14,
                          fontFamily: 'sans-serif',
                        }}
                      >
                        {(service as ParkingService).heightLimit === null
                          ? '-'
                          : (service as ParkingService).heightLimit === -1
                            ? '不限高'
                            : `限高 ${(service as ParkingService).heightLimit} cm`}
                      </Text>
                    </Group>

                    {/* 備註文字 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: '100%',
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <IconSticker2
                        size={16}
                        color={colorScheme === 'light' ? 'black' : 'white'}
                      />
                      <Tooltip
                        multiline
                        label={
                          <Text
                            style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}
                          >
                            {service.notes}
                          </Text>
                        }
                        disabled={!service.notes}
                        styles={{
                          tooltip: {
                            maxWidth: 300,
                          },
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 14,
                            fontWeight: 400,
                            maxWidth: 276,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {service.notes
                            ? `${service.notes.split('\n')[0]} ...`
                            : '-'}
                        </Text>
                      </Tooltip>
                    </Group>

                    <Group
                      justify={'space-between'}
                      style={{
                        width: 300,
                        height: 32,
                        border:
                          colorScheme === 'light'
                            ? '1px solid #DEE2E6'
                            : '1px solid #737373ff',
                        borderRadius: 4,
                        paddingInline: 16,
                        gap: 0,
                      }}
                    >
                      <Group
                        gap={8}
                        style={{
                          maxHeight: 19,
                          maxWidth: 230,
                          overflow: 'hidden',
                        }}
                      >
                        <IconParking
                          color={'#228BE6'}
                          style={{
                            backgroundColor: '#228BE61A',
                            width: 16,
                            height: 16,
                          }}
                        />
                        <div style={{ fontSize: 12, fontWeight: 400 }}>
                          總車位數
                        </div>
                      </Group>
                      <div style={{ fontSize: 12, fontWeight: 400 }}>
                        {'數量 '}
                        {(service as ParkingService).parkingSpaceInfo
                          .totalSpacesSum !== null
                          ? (service as ParkingService).parkingSpaceInfo
                              .totalSpacesSum
                          : '未知'}
                      </div>
                    </Group>
                  </Stack>
                );
              } else if (service.serviceType === 'FUELING') {
                return (
                  <Stack
                    key={index}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 15,
                      paddingTop: numOfServices !== 0 ? 15 : 6,
                      paddingBottom: 20,
                      borderBottom:
                        index !== numOfServices - 1 && colorScheme === 'light'
                          ? '1px solid #DEE2E6'
                          : index !== numOfServices - 1 &&
                              colorScheme === 'dark'
                            ? '1px solid #737373ff'
                            : null,
                    }}
                  >
                    {/* 服務狀態 與 名稱 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: 300,
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: `${getServiceStatusColor(service.serviceStatus)}`,
                        }}
                      />
                      <IconGasStation size={16} />
                      <Tooltip
                        label={service.name}
                        position={'bottom'}
                        offset={0}
                        openDelay={200}
                        color={'rgba(120, 120, 120, 0.85)'}
                        style={{
                          fontSize: 12,
                        }}
                      >
                        <Text
                          truncate={'end'}
                          style={{
                            fontWeight: 700,
                            fontSize: 14,
                            fontFamily: 'sans-serif',
                            maxWidth: 214,
                            maxHeight: 20,
                          }}
                        >
                          {service.name}
                        </Text>
                      </Tooltip>
                    </Group>

                    {/* 品牌 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: '100%',
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <IconVector
                        size={16}
                        color={colorScheme === 'light' ? 'black' : 'white'}
                      />
                      <Text
                        style={{
                          fontWeight: 400,
                          fontSize: 14,
                          fontFamily: 'sans-serif',
                        }}
                      >
                        {brandQueries[index]?.data?.name ?? '-'}
                      </Text>
                    </Group>

                    {/* 電話號碼 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: '100%',
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <IconPhone
                        size={16}
                        color={colorScheme === 'light' ? 'black' : 'white'}
                      />
                      <Text
                        style={{
                          fontWeight: 400,
                          fontSize: 14,
                          fontFamily: 'sans-serif',
                        }}
                      >
                        {service.phoneNumber
                          ? (() => {
                              if (
                                service.phoneNumber.countryCode === '' ||
                                service.phoneNumber.number === ''
                              ) {
                                return '-';
                              }

                              const parsedPhoneNumber = phoneUtil.parse(
                                `${service.phoneNumber.countryCode}${service.phoneNumber.number}`,
                              );

                              return (
                                <>
                                  {'('}
                                  {service.phoneNumber.countryCode}
                                  {') '}
                                  {phoneUtil.format(
                                    parsedPhoneNumber,
                                    PhoneNumberFormat.NATIONAL,
                                  )}
                                </>
                              );
                            })()
                          : '-'}
                      </Text>
                    </Group>

                    {/* 營業時間 */}
                    <Accordion
                      variant="unstyled"
                      value={operationHoursOpen}
                      onChange={setOperationHoursOpen}
                      styles={{
                        root: {
                          padding: 0,
                          gap: 8,
                          width: 168,
                        },
                        label: {
                          height: 20,
                          padding: 0,
                          fontSize: 14,
                          marginRight: 8,
                        },
                        control: {
                          padding: 0,
                        },
                        content: {
                          fontSize: 12,
                          fontWeight: 400,
                          paddingLeft: 24,
                          paddingRight: 0,
                          marginTop: 10,
                          marginBottom: 5,
                          paddingBottom: 0,
                        },
                        icon: {
                          marginRight: 8,
                        },
                      }}
                    >
                      <Accordion.Item value={'operation-hours'}>
                        <Accordion.Control
                          icon={
                            <IconClock
                              size={16}
                              color={
                                colorScheme === 'light' ? 'black' : 'white'
                              }
                            />
                          }
                        >
                          查看營業時間
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack gap={10}>
                            {Object.entries(dayMap).map(([key, { label }]) => {
                              const schedule =
                                service.operationHours?.weeklySchedule ?? [];
                              const day = schedule.find(
                                (w) => w.dayOfWeek === key,
                              );

                              // day 有可能不存在
                              const isUnknown = day?.isOpen === 'UNKNOWN';
                              const isOpen = day?.isOpen === 'OPEN';

                              const start =
                                day?.timeSlots?.[0]?.startTime
                                  ?.split(':')
                                  .slice(0, 2)
                                  .join(':') ?? '--:--';

                              const end =
                                day?.timeSlots?.[0]?.endTime
                                  ?.split(':')
                                  .slice(0, 2)
                                  .join(':') ?? '--:--';

                              return (
                                <Group key={key} gap={3}>
                                  {isOpen ? (
                                    <>
                                      <Text
                                        style={{
                                          fontSize: 12,
                                          whiteSpace: 'pre',
                                        }}
                                      >
                                        {label}
                                        {'   '}
                                        {start}
                                        {' - '}
                                        {end}
                                      </Text>
                                    </>
                                  ) : isUnknown ? (
                                    <Text
                                      style={{
                                        color: 'grey',
                                        fontSize: 12,
                                        whiteSpace: 'pre',
                                      }}
                                    >
                                      {label}
                                      {'   '}
                                      未知
                                    </Text>
                                  ) : (
                                    <Text
                                      style={{
                                        color: 'grey',
                                        fontSize: 12,
                                        whiteSpace: 'pre',
                                      }}
                                    >
                                      {label}
                                      {'   '}
                                      休息
                                    </Text>
                                  )}
                                </Group>
                              );
                            })}
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>

                    {/* 支援油種 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: '100%',
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <IconSticker2
                        size={16}
                        color={colorScheme === 'light' ? 'black' : 'white'}
                      />
                      <Text
                        style={{
                          fontWeight: 400,
                          fontSize: 14,
                          fontFamily: 'sans-serif',
                        }}
                      >
                        {(service as FuelingService).supportedFuelTypes
                          .length === 0
                          ? '未知'
                          : [...(service as FuelingService).supportedFuelTypes]
                              .sort(
                                (a, b) =>
                                  fuelTypeMap[a].order - fuelTypeMap[b].order,
                              )
                              .map((fuelType) => fuelTypeMap[fuelType].label)
                              .join(' | ')}
                      </Text>
                    </Group>

                    {/* 備註文字 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: '100%',
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <IconSticker2
                        size={16}
                        color={colorScheme === 'light' ? 'black' : 'white'}
                      />
                      <Tooltip
                        multiline
                        label={
                          <Text
                            style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}
                          >
                            {service.notes}
                          </Text>
                        }
                        disabled={!service.notes}
                        styles={{
                          tooltip: {
                            maxWidth: 300,
                          },
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 14,
                            fontWeight: 400,
                            maxWidth: 276,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {service.notes
                            ? `${service.notes.split('\n')[0]} ...`
                            : '-'}
                        </Text>
                      </Tooltip>
                    </Group>
                  </Stack>
                );
              } else if (
                service.serviceType === 'MAINTENANCE' ||
                service.serviceType === 'INSPECTION'
              ) {
                return (
                  <Stack
                    key={index}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 15,
                      paddingTop: numOfServices !== 0 ? 15 : 6,
                      paddingBottom: 20,
                      borderBottom:
                        index !== numOfServices - 1 && colorScheme === 'light'
                          ? '1px solid #DEE2E6'
                          : index !== numOfServices - 1 &&
                              colorScheme === 'dark'
                            ? '1px solid #737373ff'
                            : null,
                    }}
                  >
                    {/* 服務狀態 與 名稱 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: 300,
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: `${getServiceStatusColor(service.serviceStatus)}`,
                        }}
                      />
                      {service.serviceType === 'MAINTENANCE' ? (
                        <IconTool size={16} />
                      ) : (
                        <IconClipboardCheck size={16} />
                      )}
                      <Tooltip
                        label={service.name}
                        position={'bottom'}
                        offset={0}
                        openDelay={200}
                        color={'rgba(120, 120, 120, 0.85)'}
                        style={{
                          fontSize: 12,
                        }}
                      >
                        <Text
                          truncate={'end'}
                          style={{
                            fontWeight: 700,
                            fontSize: 14,
                            fontFamily: 'sans-serif',
                            maxWidth: 214,
                            maxHeight: 20,
                          }}
                        >
                          {service.name}
                        </Text>
                      </Tooltip>
                    </Group>

                    {/* 品牌 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: '100%',
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <IconVector
                        size={16}
                        color={colorScheme === 'light' ? 'black' : 'white'}
                      />
                      <Text
                        style={{
                          fontWeight: 400,
                          fontSize: 14,
                          fontFamily: 'sans-serif',
                        }}
                      >
                        {brandQueries[index]?.data?.name ?? '-'}
                      </Text>
                    </Group>

                    {/* 電話號碼 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: '100%',
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <IconPhone
                        size={16}
                        color={colorScheme === 'light' ? 'black' : 'white'}
                      />
                      <Text
                        style={{
                          fontWeight: 400,
                          fontSize: 14,
                          fontFamily: 'sans-serif',
                        }}
                      >
                        {service.phoneNumber
                          ? (() => {
                              if (
                                service.phoneNumber.countryCode === '' ||
                                service.phoneNumber.number === ''
                              ) {
                                return '-';
                              }

                              const parsedPhoneNumber = phoneUtil.parse(
                                `${service.phoneNumber.countryCode}${service.phoneNumber.number}`,
                              );

                              return (
                                <>
                                  {'('}
                                  {service.phoneNumber.countryCode}
                                  {') '}
                                  {phoneUtil.format(
                                    parsedPhoneNumber,
                                    PhoneNumberFormat.NATIONAL,
                                  )}
                                </>
                              );
                            })()
                          : '-'}
                      </Text>
                    </Group>

                    {/* 營業時間 */}
                    <Accordion
                      variant="unstyled"
                      value={operationHoursOpen}
                      onChange={setOperationHoursOpen}
                      styles={{
                        root: {
                          padding: 0,
                          gap: 8,
                          width: 168,
                        },
                        label: {
                          height: 20,
                          padding: 0,
                          fontSize: 14,
                          marginRight: 8,
                        },
                        control: {
                          padding: 0,
                        },
                        content: {
                          fontSize: 12,
                          fontWeight: 400,
                          paddingLeft: 24,
                          paddingRight: 0,
                          marginTop: 10,
                          marginBottom: 5,
                          paddingBottom: 0,
                        },
                        icon: {
                          marginRight: 8,
                        },
                      }}
                    >
                      <Accordion.Item value={'operation-hours'}>
                        <Accordion.Control
                          icon={
                            <IconClock
                              size={16}
                              color={
                                colorScheme === 'light' ? 'black' : 'white'
                              }
                            />
                          }
                        >
                          查看營業時間
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack gap={10}>
                            {Object.entries(dayMap).map(([key, { label }]) => {
                              const schedule =
                                service.operationHours?.weeklySchedule ?? [];
                              const day = schedule.find(
                                (w) => w.dayOfWeek === key,
                              );

                              // day 有可能不存在
                              const isUnknown = day?.isOpen === 'UNKNOWN';
                              const isOpen = day?.isOpen === 'OPEN';

                              const start =
                                day?.timeSlots?.[0]?.startTime
                                  ?.split(':')
                                  .slice(0, 2)
                                  .join(':') ?? '--:--';

                              const end =
                                day?.timeSlots?.[0]?.endTime
                                  ?.split(':')
                                  .slice(0, 2)
                                  .join(':') ?? '--:--';

                              return (
                                <Group key={key} gap={3}>
                                  {isOpen ? (
                                    <>
                                      <Text
                                        style={{
                                          fontSize: 12,
                                          whiteSpace: 'pre',
                                        }}
                                      >
                                        {label}
                                        {'   '}
                                        {start}
                                        {' - '}
                                        {end}
                                      </Text>
                                    </>
                                  ) : isUnknown ? (
                                    <Text
                                      style={{
                                        color: 'grey',
                                        fontSize: 12,
                                        whiteSpace: 'pre',
                                      }}
                                    >
                                      {label}
                                      {'   '}
                                      未知
                                    </Text>
                                  ) : (
                                    <Text
                                      style={{
                                        color: 'grey',
                                        fontSize: 12,
                                        whiteSpace: 'pre',
                                      }}
                                    >
                                      {label}
                                      {'   '}
                                      休息
                                    </Text>
                                  )}
                                </Group>
                              );
                            })}
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>

                    {/* 備註文字 */}
                    <Group
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'left',
                        width: '100%',
                        height: 20,
                        gap: 8,
                      }}
                    >
                      <IconSticker2
                        size={16}
                        color={colorScheme === 'light' ? 'black' : 'white'}
                      />
                      <Tooltip
                        multiline
                        label={
                          <Text
                            style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}
                          >
                            {service.notes}
                          </Text>
                        }
                        disabled={!service.notes}
                        styles={{
                          tooltip: {
                            maxWidth: 300,
                          },
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 14,
                            fontWeight: 400,
                            maxWidth: 276,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {service.notes
                            ? `${service.notes.split('\n')[0]} ...`
                            : '-'}
                        </Text>
                      </Tooltip>
                    </Group>
                  </Stack>
                );
              }
            })}
        </Stack>
      </div>

      {/* 下方陰影 */}
      {showBottomShadow && (
        <div
          style={{
            position: 'absolute',
            width: 340,
            justifyItems: 'left',
            bottom: 70,
            left: 0,
            right: 0,
            height: '15px',
            background:
              'linear-gradient(to top, rgba(0, 0, 0, 0.1), transparent)',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        />
      )}

      {/* 固定在底部的按鈕 */}
      <Group
        style={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          paddingInline: 25,
          borderRadius: '0 0 20px 20px',
          height: 70,
          alignItems: 'center',
          borderTop: '1px solid',
          borderColor: colorScheme === 'light' ? '#eee' : '#595959',
          backgroundColor: colorScheme === 'light' ? '#ffffff' : '#1a1a1a',
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
        }}
      >
        <Button
          component={'a'}
          href={selectedLocation.googleStreetViewInfo.url}
          target={'_blank'}
          variant={'outline'}
          style={{
            height: 40,
            width: 132,
            fontSize: 16,
            fontWeight: 400,
          }}
        >
          Google 街景
        </Button>
        <Button
          variant={'filled'}
          onClick={() => handlePlaceInformationPageClick(selectedLocation)}
          style={{
            height: 40,
            width: 132,
            fontSize: 16,
            fontWeight: 400,
          }}
        >
          查看資訊
        </Button>
      </Group>
    </div>
  );
};

export default PlaceServicesSummary;
